import React, { useEffect, useState } from "react";
import CancellationPolicyBox from "../../../components/CancellationPolicyBox";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  CancelBooking,
  GetRefundPreview,
  MarkMovedIn,
} from "../../../redux/reducer/FlatInterestRequest";

const money = (value) => `Rs ${Number(value || 0).toLocaleString()}`;

// The interest form stores DD-MM-YYYY.
const parseMoveIn = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{2})-(\d{2})-(\d{4})$/);
  const date = match
    ? new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]))
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDate = (date) =>
  date
    ? date.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "—";

const daysUntil = (date) => {
  if (!date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 864e5);
};

/**
 * The gap between "the owner verified my payment" and "I actually live here".
 *
 * That gap is often weeks long and used to be invisible — verifying the
 * payment made the tenant live immediately. Now the booking waits here, and
 * the tenant gets the two things they need in that window: when they are
 * moving in, and a way out with the refund figure stated up front rather than
 * discovered afterwards.
 */
export default function MoveInPanel({ request, onChanged }) {
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [reason, setReason] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [telling, setTelling] = useState(false);
  // Held locally so the confirmation sticks immediately, without waiting for
  // the list to refetch.
  const [toldNow, setToldNow] = useState(false);
  // Shown in the panel itself rather than only as a toast. MyRequests mounts
  // no ToastContainer, so a toast fired from here goes nowhere — pressing the
  // button looked like it did nothing at all, success or failure.
  const [error, setError] = useState("");

  const requestId = request?._id || request?.id;
  const alreadyTold = toldNow || Boolean(request?.tenantMarkedMoveInAt);

  useEffect(() => {
    if (!dialogOpen || !requestId) return;

    setLoadingPreview(true);
    dispatch(GetRefundPreview({ requestId })).then((res) => {
      setPreview(res?.payload?.data || null);
      setLoadingPreview(false);
    });
  }, [dialogOpen, requestId, dispatch]);

  if (request?.moveInStatus === "moved_in") {
    return (
      <Alert
        severity="success"
        icon={<CheckCircleIcon />}
        sx={{ borderRadius: 2, fontSize: "0.85rem", mb: 2 }}
      >
        Move-in confirmed by the owner. Your tenancy is active.
      </Alert>
    );
  }

  if (request?.moveInStatus === "cancelled") {
    const c = request.cancellation || {};
    if (c.isCash) {
      return (
        <Alert severity="info" sx={{ borderRadius: 2, fontSize: "0.85rem", mb: 2 }}>
          Booking cancelled.
          {c.reason ? ` ${c.reason}.` : ""} You paid in cash directly to the
          owner, so OTS did not receive that money and is not responsible for
          it. Any refund of that cash is settled directly between you and the
          owner.
          {c.otsFeeRefund && (
            <>
              {" "}
              The OTS service fee you paid to OTS is refunded:{" "}
              <strong>{money(c.refundAmount)}</strong> of {money(c.paidAmount)} (
              {c.percent}% — {c.bandLabel}).
              {c.refundStatus === "completed"
                ? " Refund settled."
                : " It is being processed back to you."}
            </>
          )}
        </Alert>
      );
    }

    return (
      <Alert severity="info" sx={{ borderRadius: 2, fontSize: "0.85rem", mb: 2 }}>
        Booking cancelled. Refund due: <strong>{money(c.refundAmount)}</strong>{" "}
        of {money(c.paidAmount)} ({c.percent}% — {c.bandLabel}).
        {c.refundStatus === "completed"
          ? " Refund settled."
          : " Being processed back to your original payment method."}
      </Alert>
    );
  }

  if (request?.moveInStatus !== "awaiting") return null;

  const moveInDate = parseMoveIn(request.moveInDate);
  const days = daysUntil(moveInDate);

  const countdown =
    days === null
      ? "Move-in date not set"
      : days > 1
      ? `In ${days} days`
      : days === 1
      ? "Tomorrow"
      : days === 0
      ? "Today"
      : `${Math.abs(days)} day${Math.abs(days) > 1 ? "s" : ""} ago`;

  const handleMarkMovedIn = async () => {
    setTelling(true);
    setError("");
    const res = await dispatch(
      MarkMovedIn({ requestId, tenantId: request.tenantId })
    );
    setTelling(false);

    if (res?.payload?.success) {
      toast.success(res.payload.message || "Your owner has been notified");
      setToldNow(true);
      onChanged?.();
    } else {
      const message =
        typeof res?.payload?.message === "string"
          ? res.payload.message
          : "Could not reach your owner. Please try again in a moment.";
      setError(message);
      toast.error(message);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    const res = await dispatch(
      CancelBooking({
        requestId,
        tenantId: request.tenantId,
        reason: reason.trim(),
      })
    );
    setCancelling(false);

    if (res?.payload?.success) {
      toast.success(res.payload.message || "Booking cancelled");
      setDialogOpen(false);
      onChanged?.();
    } else {
      const message =
        typeof res?.payload?.message === "string"
          ? res.payload.message
          : "Could not cancel the booking. Please try again in a moment.";
      setError(message);
      setDialogOpen(false);
      toast.error(message);
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: "1px solid #bfdbfe",
          bgcolor: "#eff6ff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <EventAvailableIcon sx={{ color: "#1d4ed8", fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Moving in
          </Typography>
          <Chip
            size="small"
            label={countdown}
            sx={{
              ml: "auto",
              fontWeight: 700,
              fontSize: 11,
              color: "#1d4ed8",
              bgcolor: "#dbeafe",
            }}
          />
        </Box>

        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
          {formatDate(moveInDate)}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.6 }}>
          The property is held for you. Your owner confirms the move-in once you
          are in — bills, maintenance and rent payment unlock at that point.
        </Typography>

        {/* Your owner is not standing in the property; you are. Telling them
            is what puts the confirmation in front of them. */}
        {alreadyTold ? (
          <Alert
            severity="info"
            sx={{ mt: 1.4, fontSize: "0.82rem", py: 0.4 }}
          >
            Your owner has been told you've moved in. Waiting on their
            confirmation.
          </Alert>
        ) : (
          <Button
            variant="contained"
            size="small"
            startIcon={
              telling ? (
                <CircularProgress size={15} color="inherit" />
              ) : (
                <MeetingRoomIcon />
              )
            }
            disabled={telling}
            onClick={handleMarkMovedIn}
            sx={{
              mt: 1.4,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "999px",
              px: 2.2,
            }}
          >
            {telling ? "Telling your owner…" : "I've moved in"}
          </Button>
        )}

        <Button
          size="small"
          onClick={() => setDialogOpen(true)}
          sx={{
            mt: 1.4,
            ml: alreadyTold ? 0 : 1,
            textTransform: "none",
            fontWeight: 700,
            color: "#b91c1c",
          }}
        >
          Cancel booking
        </Button>

        {error && (
          <Alert
            severity="error"
            onClose={() => setError("")}
            sx={{ mt: 1.4, fontSize: "0.82rem" }}
          >
            {error}
          </Alert>
        )}
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={() => !cancelling && setDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Cancel this booking?</DialogTitle>

        <DialogContent>
          {loadingPreview ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={26} />
            </Box>
          ) : preview?.isCash ? (
            <>
              {/* Cash went straight to the owner and never reached OTS, so
                  there is no refund figure to show. */}
              <Alert severity="info" sx={{ fontSize: "0.82rem" }}>
                You paid in cash directly to the owner. OTS did not receive that
                money and is not responsible for it — any refund of that cash
                has to be settled directly between you and the owner.
              </Alert>

              {/* The one part of a cash booking OTS does hold: its own fee,
                  once an admin has verified it. That much is refunded. */}
              {preview.feeOnly && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    border: "1px solid #e5e7eb",
                    borderRadius: 1.5,
                  }}
                >
                  <Typography variant="body2" fontWeight={700}>
                    OTS service fee refund
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {preview.bandLabel} — {preview.percent}% of the fee you paid
                    OTS comes back.
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.8 }}>
                    <Typography variant="body2">You paid OTS</Typography>
                    <Typography variant="body2">{money(preview.paidAmount)}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">Deducted</Typography>
                    <Typography variant="body2">{money(preview.deductedAmount)}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                    <Typography variant="body2" fontWeight={700}>
                      You get back
                    </Typography>
                    <Typography sx={{ fontWeight: 800, color: "#15803d" }}>
                      {money(preview.refundAmount)}
                    </Typography>
                  </Box>
                </Box>
              )}

              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                placeholder="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                sx={{ mt: 2 }}
              />

              <Alert severity="warning" sx={{ mt: 2, fontSize: "0.8rem" }}>
                This frees the property for other tenants and cannot be undone.
              </Alert>
            </>
          ) : preview ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {preview.band === "grace"
                  ? `${preview.bandLabel} — everything you paid comes back.`
                  : preview.band === "released"
                  ? preview.reason
                  : `${preview.bandLabel} — ${preview.percent}% of your advance comes back. OTS's commission and the processing fee are not refunded.`}
              </Typography>

              <Stack spacing={1}>
                {[
                  ["You paid", money(preview.paidAmount)],
                  ["Deducted", money(preview.deductedAmount)],
                ].map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      py: 0.8,
                      borderBottom: "1px solid #eef1f6",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {label}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {value}
                    </Typography>
                  </Box>
                ))}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    pt: 0.6,
                  }}
                >
                  <Typography variant="body2" fontWeight={700}>
                    You get back
                  </Typography>
                  <Typography sx={{ fontWeight: 800, color: "#15803d" }}>
                    {money(preview.refundAmount)}
                  </Typography>
                </Box>
              </Stack>

              {!preview.feeOnly && preview.ladder && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase" }}>
                    The cancellation terms your booking follows
                  </Typography>
                  <CancellationPolicyBox steps={preview.ladder} dense />
                </Box>
              )}

              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                placeholder="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                sx={{ mt: 2 }}
              />

              <Alert severity="warning" sx={{ mt: 2, fontSize: "0.8rem" }}>
                This frees the property for other tenants and cannot be undone.
              </Alert>
            </>
          ) : (
            <Alert severity="error">We could not work out your refund right now. Please close this window and try again.</Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={cancelling}
            sx={{ textTransform: "none" }}
          >
            Keep booking
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={cancelling || loadingPreview || !preview?.cancellable}
            onClick={handleCancel}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {cancelling ? (
              <>
                <CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />
                Cancelling…
              </>
            ) : (
              "Cancel booking"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
